import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, FormControlLabel, Checkbox, Box } from '@mui/material';
import { Button } from 'react-bootstrap';
import T from 'i18n';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DatePicker from 'features/common/Filters/DatePicker';
import { useDispatch } from 'react-redux';
import { clearSearchedFiles } from 'features/case/redux/fetchSearchedFiles';

interface PresentViewClearSearchPopupProps {
  open: boolean;
  onClose: () => void;
  caseId: string;
  setRecentSearches?: React.Dispatch<React.SetStateAction<any>>;
}

interface DateRangeState {
  start: Date | null;
  end: Date | null;
  min: Date | null;
  max: Date | null;
}

const PresentViewClearSearchPopup: React.FC<PresentViewClearSearchPopupProps> = ({
  open,
  onClose,
  caseId,
  setRecentSearches,
}) => {
  const [useDateRange, setUseDateRange] = useState<boolean>(false);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);
  const dispatch = useDispatch();

  const [dateRange, setDateRange] = useState<DateRangeState>({
    start: null,
    end: null,
    min: null,
    max: null,
  });

  useEffect(() => {
    if (open) {
      setUseDateRange(false);

      const storedSearches = localStorage.getItem(`${caseId}.recentSearches`);
      if (storedSearches) {
        const searches = JSON.parse(storedSearches);
        if (Object.keys(searches).length > 0) {
          const timestamps = Object.values(searches).map((doc: any) => doc.timestamp);
          const oldestTimestamp = Math.min(...timestamps);
          const newestTimestamp = Math.max(...timestamps);

          const oldestDate = new Date(oldestTimestamp);
          const newestDate = new Date(newestTimestamp);

          setDateRange({
            start: oldestDate,
            end: newestDate,
            min: oldestDate,
            max: newestDate,
          });
        }
      } else {
        const today = new Date();
        setDateRange({
          start: today,
          end: today,
          min: today,
          max: today,
        });
      }

      setIsButtonDisabled(false);
    }
  }, [open, caseId]);

  useEffect(() => {
    setIsButtonDisabled(useDateRange && (!dateRange.start || !dateRange.end));
  }, [useDateRange, dateRange.start, dateRange.end]);

  const handleClear = () => {
    // Clear Redux cache for searched files
    dispatch(clearSearchedFiles());

    if (!useDateRange) {
      if (setRecentSearches) {
        setRecentSearches({});
      }

      localStorage.setItem(`${caseId}.recentSearches`, JSON.stringify({}));

      // Clear session storage as well
      sessionStorage.removeItem(`${caseId}.searchValue`);
      sessionStorage.removeItem(`${caseId}.docsToDisplay`);
      sessionStorage.removeItem(`${caseId}.currentlyPresenting`);

      onClose();
    } else {
      const startDateTime = new Date(dateRange.start!);
      startDateTime.setHours(0, 0, 0, 0);
      const startTimestamp = startDateTime.getTime();

      const endDateTime = new Date(dateRange.end!);
      endDateTime.setHours(23, 59, 59, 999);
      const endTimestamp = endDateTime.getTime();

      // Get current searches from localStorage
      const storedSearches = localStorage.getItem(`${caseId}.recentSearches`);
      if (!storedSearches) {
        onClose();
        return;
      }

      const searches = JSON.parse(storedSearches);
      const filteredSearches = { ...searches };
      let hasRemovedItems = false;

      Object.entries(filteredSearches).forEach(([key, doc]: [string, any]) => {
        const itemTimestamp = doc.timestamp;

        if (itemTimestamp >= startTimestamp && itemTimestamp <= endTimestamp) {
          delete filteredSearches[key];
          hasRemovedItems = true;
        }
      });

      if (hasRemovedItems) {
        localStorage.setItem(`${caseId}.recentSearches`, JSON.stringify(filteredSearches));
        if (setRecentSearches) {
          setRecentSearches(filteredSearches);
        }
      }
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{T.translate('case.presentView.clearAllDocsModal.title')}</DialogTitle>
      <DialogContent>
        <div style={{ marginBottom: '16px' }}>
          {T.translate('case.presentView.clearAllDocsModal.content')}
        </div>

        <FormControlLabel
          control={
            <Checkbox checked={useDateRange} onChange={(e) => setUseDateRange(e.target.checked)} />
          }
          label={T.translate('case.presentView.clearAllDocsModal.useDateRange')}
        />

        {useDateRange && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 2,
                marginTop: '12px',
                marginBottom: '16px',
              }}
            >
              <Box sx={{ width: '45%' }}>
                <DatePicker
                  label={T.translate('case.presentView.clearAllDocsModal.fromDate', {
                    defaultValue: 'From Date',
                  })}
                  value={dateRange.start}
                  format="dd MMMM yyyy"
                  onChange={(dateVal: React.SetStateAction<Date | null>) => {
                    setDateRange((prev) => ({ ...prev, start: dateVal as Date | null }));
                  }}
                  minDate={dateRange.min}
                  maxDate={dateRange.end || dateRange.max}
                  disableFuture={false}
                  disablePast={false}
                  shouldDisableDate={(date: any) => {
                    if (!date || !dateRange.min) return true;
                    const dateToCheck = new Date(date);
                    dateToCheck.setHours(0, 0, 0, 0);

                    const minDateStart = new Date(dateRange.min);
                    minDateStart.setHours(0, 0, 0, 0);

                    const maxDateToCheck = dateRange.end || dateRange.max;
                    const maxDateStart = maxDateToCheck ? new Date(maxDateToCheck) : null;
                    if (maxDateStart) maxDateStart.setHours(0, 0, 0, 0);

                    return (
                      dateToCheck < minDateStart || (maxDateStart && dateToCheck > maxDateStart)
                    );
                  }}
                  disableHighlightToday
                  sx={{ width: '100%' }}
                />
              </Box>
              <Box sx={{ width: '45%' }}>
                <DatePicker
                  label={T.translate('case.presentView.clearAllDocsModal.toDate', {
                    defaultValue: 'To Date',
                  })}
                  value={dateRange.end}
                  format="dd MMMM yyyy"
                  onChange={(dateVal: React.SetStateAction<Date | null>) => {
                    setDateRange((prev) => ({ ...prev, end: dateVal as Date | null }));
                  }}
                  minDate={dateRange.start || dateRange.min}
                  maxDate={dateRange.max}
                  disableFuture={false}
                  disablePast={false}
                  shouldDisableDate={(date: any) => {
                    if (!date || !dateRange.max) return true;

                    const dateToCheck = new Date(date);
                    dateToCheck.setHours(0, 0, 0, 0);

                    const minDateToCheck = dateRange.start || dateRange.min;
                    const minDateStart = minDateToCheck ? new Date(minDateToCheck) : null;
                    if (minDateStart) minDateStart.setHours(0, 0, 0, 0);

                    const maxDateStart = new Date(dateRange.max);
                    maxDateStart.setHours(0, 0, 0, 0);

                    return (
                      (minDateStart && dateToCheck < minDateStart) || dateToCheck > maxDateStart
                    );
                  }}
                  disableHighlightToday
                  sx={{ width: '100%' }}
                />
              </Box>
            </Box>
          </LocalizationProvider>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '15px', marginTop: 16 }}>
          <Button variant="secondary" onClick={onClose} className="cancel-button">
            {T.translate('case.presentView.clearAllDocsModal.cancel')}
          </Button>
          <Button onClick={handleClear} className="clear-button" disabled={isButtonDisabled}>
            {T.translate('case.presentView.clearAllDocsModal.clear')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PresentViewClearSearchPopup;
